import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getProducts } from '../services/productService';
import FilterPanel from '../components/marketplace/FilterPanel';
import { ArrowDownRight, ArrowRight, CircleHelp, LoaderCircle, ShieldCheck, Sparkles, TrendingUp, Users } from 'lucide-react';
import {
  PLATFORMS,
  BadgeCheckIcon,
  extractProductList,
  ListingCard
} from './marketplaceShared';

export {
  PLATFORMS,
  VISIBLE_PLATFORMS,
  CATEGORIES,
  SORT_OPTIONS,
  formatPrice,
  formatFollowers,
  extractProductList,
  ShieldCheckIcon,
  BadgeCheckIcon,
  PlatformGlyph,
  getListingImage,
  ListingCard
} from './marketplaceShared';

export default function Home() {
  const location = useLocation();
  const urlPlatform = useMemo(() => {
    const requested = new URLSearchParams(location.search).get('platform') || 'all';
    const match = PLATFORMS.find(platform => platform.toLowerCase() === requested.toLowerCase());
    return match || 'All';
  }, [location.search]);

  const [activePlatforms, setActivePlatforms] = useState(urlPlatform === 'All' ? [] : [urlPlatform]);
  const [searchName, setSearchName] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [monetizedOnly, setMonetizedOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [followerRange, setFollowerRange] = useState({ min: '', max: '' });
  const [incomeRange, setIncomeRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('Newest first');
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState('');

  useEffect(() => {
    setActivePlatforms(urlPlatform === 'All' ? [] : [urlPlatform]);
  }, [urlPlatform]);

  useEffect(() => {
    let mounted = true;

    async function loadProducts() {
      setLoadingProducts(true);
      setProductsError('');
      try {
        const response = await getProducts();
        if (mounted) setProducts(extractProductList(response));
      } catch (error) {
        console.error('Unable to load listings', error);
        if (mounted) setProductsError('Unable to load listings right now.');
      } finally {
        if (mounted) setLoadingProducts(false);
      }
    }

    loadProducts();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredProducts = useMemo(() => {
    const filtered = products.filter(item => {
      const price = Number(item.price) || 0;
      const followers = Number(item.followers) || 0;
      const income = Number(item.income || item.monthlyIncome) || 0;
      if (activePlatforms.length > 0 && !activePlatforms.includes(item.platform)) return false;
      if (searchName && !String(item.title || '').toLowerCase().includes(searchName.toLowerCase())) return false;
      if (priceRange.min !== '' && price < Number(priceRange.min)) return false;
      if (priceRange.max !== '' && price > Number(priceRange.max)) return false;
      if (followerRange.min !== '' && followers < Number(followerRange.min)) return false;
      if (followerRange.max !== '' && followers > Number(followerRange.max)) return false;
      if (incomeRange.min !== '' && income < Number(incomeRange.min)) return false;
      if (incomeRange.max !== '' && income > Number(incomeRange.max)) return false;
      if (activeCategory !== 'All' && item.topic !== activeCategory) return false;
      if (monetizedOnly && (item.platform !== 'YouTube' || !item.monetized)) return false;
      if (verifiedOnly && item.verified === false) return false;
      return true;
    });

    const sorted = [...filtered];
    if (sortBy === 'Price: Low to High') sorted.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
    else if (sortBy === 'Price: High to Low') sorted.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
    else if (sortBy === 'Most followers') sorted.sort((a, b) => (Number(b.followers) || 0) - (Number(a.followers) || 0));
    else sorted.reverse();
    return sorted;
  }, [activePlatforms, searchName, priceRange.max, priceRange.min, followerRange.max, followerRange.min, incomeRange.max, incomeRange.min, activeCategory, monetizedOnly, verifiedOnly, sortBy, products]);

  const visibleProducts = useMemo(() => filteredProducts.slice(0, 8), [filteredProducts]);
  const featuredListings = visibleProducts;
  const heroProduct = featuredListings[0] || products[0];

  function clearFilters() {
    setActivePlatforms([]);
    setSearchName('');
    setActiveCategory('All');
    setMonetizedOnly(false);
    setVerifiedOnly(false);
    setPriceRange({ min: '', max: '' });
    setFollowerRange({ min: '', max: '' });
    setIncomeRange({ min: '', max: '' });
    setSortBy('Newest first');
  }

  return (
    <div className="home-page">
      <section className="hero-section">
        <div className="hero-copy">
          <span className="hero-kicker">
            <span className="kicker-mark"><Sparkles size={13} /></span>
            <span>Premium Escrow Exchange</span>
            <span className="kicker-line" />
          </span>
          <h1>Buy &amp; sell <span>verified</span> digital communities.</h1>
          <p className="hero-description">
            The private marketplace for monetized YouTube channels, high-engagement Instagram portfolios, TikTok creator brands, and established communities with verified analytics.
          </p>
          <div className="hero-cta-row">
            <a className="button button-dark" href="#marketplace">
              <span>Explore Marketplace</span> <ArrowDownRight size={16} />
            </a>
            <Link className="button button-outline" to="/upload">
              <span>List Your Community</span> <ArrowRight size={16} />
            </Link>
          </div>
          <div className="hero-proof">
            <ShieldCheck size={20} color="var(--cyan)" />
            <p>
              <strong>100% Escrow Protected</strong>
              <span>Funds held securely until complete ownership transfer</span>
            </p>
          </div>
        </div>
        <div className="hero-art">
          <div className="art-orbit orbit-one" aria-hidden="true" />
          <div className="art-orbit orbit-two" aria-hidden="true" />
          <div className="art-stamp" aria-hidden="true">VERIFIED<br />ESCROW</div>
          <div className="floating-note" aria-hidden="true">
            <div className="note-icon"><TrendingUp size={16} /></div>
            <div>
              <strong>{heroProduct?.price ? `₾${Number(heroProduct.price).toLocaleString()}` : 'Escrow protected'}</strong>
              <small>{heroProduct?.price ? 'Verified listing price' : 'Secure transfers'}</small>
            </div>
          </div>
          <Link
            className="hero-visual-card"
            to={heroProduct?.id ? `/product/${encodeURIComponent(heroProduct.id)}` : '/products'}
            aria-label={heroProduct?.id
              ? `View ${heroProduct.title || 'featured community'} listing`
              : 'Explore marketplace listings'}
          >
            <div className="visual-topline">
              <span><i /> Verified community</span>
              <span>{heroProduct?.platform || 'GETSOCS'}</span>
            </div>
            <div className="visual-community-art" aria-hidden="true">
              <div className="visual-sun" />
              <div className="visual-horizon" />
              <div className="visual-ring ring-a" />
              <div className="visual-ring ring-b" />
              <div className="visual-caption">{heroProduct?.title || 'Creator community'}</div>
            </div>
            <div className="visual-card-bottom">
              <div>
                <span>Marketplace</span>
                <strong>{products.length} {products.length === 1 ? 'listing' : 'listings'}</strong>
              </div>
              <div className="visual-arrow"><ArrowRight size={16} /></div>
            </div>
          </Link>
        </div>
      </section>

      <section className="ticker-band" aria-label="Marketplace benefits">
        <div className="ticker-inner">
          <span><ShieldCheck size={16} /> Escrow-protected transfers</span>
          <i />
          <span><BadgeCheckIcon /> Verified listings</span>
          <i />
          <span><Users size={16} /> Creator communities</span>
          <i />
          <span><Sparkles size={16} /> Secure ownership handoff</span>
        </div>
      </section>

      <section className="market-section" id="marketplace">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Verified assets <span className="section-dash" /></span>
            <h2>Explore <em>curated</em> communities</h2>
          </div>
          <div className="results-count"><span>{filteredProducts.length} listings available</span></div>
        </div>

        <FilterPanel
        activePlatforms={activePlatforms}
        setActivePlatforms={setActivePlatforms}
        searchName={searchName}
        setSearchName={setSearchName}
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        monetizedOnly={monetizedOnly}
        setMonetizedOnly={setMonetizedOnly}
        verifiedOnly={verifiedOnly}
        setVerifiedOnly={setVerifiedOnly}
        followerRange={followerRange}
        setFollowerRange={setFollowerRange}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        incomeRange={incomeRange}
        setIncomeRange={setIncomeRange}
        sortBy={sortBy}
        setSortBy={setSortBy}
        onClear={clearFilters}
      />

      <div className="target-dashboard-grid">
        <section className="target-market-area">
          {loadingProducts ? (
            <section className="state-panel" role="status" aria-live="polite">
              <div className="empty-glyph"><LoaderCircle size={22} className="loading-icon" /></div>
              <div>
                <strong>Loading listings</strong>
                <p>Checking the marketplace for active social accounts.</p>
              </div>
            </section>
          ) : productsError ? (
            <section className="state-panel error-panel" role="alert">
              <CircleHelp size={28} />
              <div>
                <strong>Unable to load marketplace listings</strong>
                <p>{productsError}</p>
              </div>
            </section>
          ) : featuredListings.length ? (
            <section className="target-listings-panel">
              <header>
                <div>
                  <h2>Featured Listings</h2>
                  <p className="target-listings-subtitle">Handpicked premium accounts just for you</p>
                </div>
                <Link to="/products">View all -&gt;</Link>
              </header>
              <div className="target-listing-grid">
                {featuredListings.map(item => <ListingCard key={item.id} item={item} />)}
              </div>
            </section>
          ) : (
            <section className="state-panel">
              <div className="empty-glyph"><CircleHelp size={22} /></div>
              <div>
                <strong>No listings match right now</strong>
                <p>Try widening your filters, or be the first to list an account in this category.</p>
              </div>
              <div className="empty-panel-actions">
                <button type="button" className="button button-outline" onClick={clearFilters}>Clear filters</button>
                <Link className="button button-dark" to="/upload">Sell your page</Link>
              </div>
            </section>
          )}
        </section>

      </div>
      </section>
    </div>
  );
}
